import { getLandingContent } from "@/content/landing";
import { useTenantConfig, type TenantConfig } from "@/hooks/use-tenant-config";
import { tenantSlugFromHostname, tenantStaticProfile } from "@/lib/tenant";

/** UI-ready tenant identity with all fallback decisions made in one layer. */
export interface TenantPresentation {
  content: ReturnType<typeof getLandingContent>;
  contentSlug: string;
  name: string;
  logoUrl: string | null;
  sidebarLogoUrl: string | null;
  parentOrg: string | null;
  brandingStatus: TenantConfig["brandingStatus"];
  usesFallbackIdentity: boolean;
}

export function deriveTenantPresentation(
  config: Pick<TenantConfig, "name" | "logoUrl" | "parentOrg" | "brandingStatus">,
  hostname: string,
): TenantPresentation {
  const contentSlug = tenantSlugFromHostname(hostname);
  const fallback = tenantStaticProfile(contentSlug);
  if (!contentSlug || !fallback) {
    throw new Error("Tenant unavailable");
  }
  const resolved = config.brandingStatus === "resolved";

  return {
    content: getLandingContent(contentSlug),
    contentSlug,
    name: resolved && config.name ? config.name : fallback.displayName,
    // A configured server logo wins; a missing value uses the known tenant asset.
    logoUrl: config.logoUrl ?? fallback.logoUrl,
    // Known tenants use their colour mark on the light rail. A configured server
    // logo remains authoritative and renders as supplied.
    sidebarLogoUrl: config.logoUrl ?? fallback.sidebarLogoUrl,
    parentOrg: resolved ? config.parentOrg : fallback.parentOrg,
    brandingStatus: config.brandingStatus,
    usesFallbackIdentity: !resolved || !config.name,
  };
}

export function useTenantPresentation(): TenantPresentation {
  const config = useTenantConfig();
  return deriveTenantPresentation(config, window.location.hostname);
}
